import React, {useState} from 'react';
import styled from 'styled-components';
import {Col, Empty, Input, Pagination, Row, Spin} from 'antd';
import {useNavigate} from 'react-router-dom';
import Section from '../components/Section';
import ContentContainer from '../components/ContentContainer';
import Title from '../components/Title';
import ScrollAnimation from '../components/animations/ScrollAnimation';
import ProductCard from '../components/shop/ProductCard';
import useDefaultRequest from '../hooks/useDefaultRequest';
import ENDPOINTS from '../constants/endpoints';
import {formatPath, ROUTES} from '../constants/PathRoutes';
import {BORDER_GRAY, GREE_1, GREE_2, GREE_3, LIGHT_GRAY} from '../utils/colors';

const {Search} = Input;

const PageHeader = styled.div`
    background: ${GREE_1};
    padding: 60px 0;
    text-align: center;
`;

const PageHeaderTitle = styled.h1`
    color: white;
    font-size: 36px;
    font-weight: 700;
    margin: 0 0 8px;
`;

const PageHeaderSubtitle = styled.p`
    color: rgba(255, 255, 255, 0.85);
    font-size: 16px;
    margin: 0;
`;

const FilterSection = styled.div`
    margin-bottom: 32px;
`;

const SearchWrapper = styled.div`
    margin-bottom: 20px;
    max-width: 480px;
`;

const CategoryList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const CategoryChip = styled.button`
    padding: 6px 18px;
    border-radius: 20px;
    border: 1px solid ${({active}) => active ? GREE_1 : BORDER_GRAY};
    background: ${({active}) => active ? GREE_1 : 'white'};
    color: ${({active}) => active ? 'white' : '#555'};
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: ${GREE_1};
        color: ${({active}) => active ? 'white' : GREE_1};
    }
`;

const PaginationWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 40px;
`;

const PAGE_SIZE = 12;

export default function PublicProducts() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);

    const {data: categories, isLoading: loadingCategories} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_CATEGORY_PUBLIC,
        dataDefault: [],
    });

    const {data: productsData, isLoading: loadingProducts} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_PRODUCT_PUBLIC,
        params: {
            search,
            category: selectedCategory || undefined,
            page,
            page_size: PAGE_SIZE,
        },
        dependence: [search, selectedCategory, page],
    });

    const products = productsData?.results || (Array.isArray(productsData) ? productsData : []);
    const total = productsData?.count || 0;

    const handleSearch = (value) => {
        setSearch(value);
        setPage(1);
    };

    const handleCategoryClick = (categoryId) => {
        setSelectedCategory(prev => prev === categoryId ? null : categoryId);
        setPage(1);
    };

    const handleDetail = (product) => {
        navigate(formatPath(ROUTES.PRODUTOS_DETAIL, {slug: product.slug}));
    };

    return (
        <>
            <PageHeader>
                <PageHeaderTitle>Nossos Produtos</PageHeaderTitle>
                <PageHeaderSubtitle>Conheça nossa linha de produtos de Cannabis Medicinal</PageHeaderSubtitle>
            </PageHeader>

            <Section style={{background: GREE_2}}>
                <ContentContainer>
                    <FilterSection>
                        <SearchWrapper>
                            <Search
                                placeholder="Buscar produtos..."
                                allowClear
                                size="large"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onSearch={handleSearch}
                            />
                        </SearchWrapper>

                        {!loadingCategories && categories.length > 0 && (
                            <CategoryList>
                                <CategoryChip
                                    active={selectedCategory === null ? 1 : 0}
                                    onClick={() => handleCategoryClick(null)}
                                >
                                    Todos
                                </CategoryChip>
                                {categories.map(cat => (
                                    <CategoryChip
                                        key={cat.id}
                                        active={selectedCategory === cat.id ? 1 : 0}
                                        onClick={() => handleCategoryClick(cat.id)}
                                    >
                                        {cat.name}
                                    </CategoryChip>
                                ))}
                            </CategoryList>
                        )}
                    </FilterSection>

                    {loadingProducts ? (
                        <div style={{textAlign: 'center', padding: '60px 0'}}>
                            <Spin size="large"/>
                        </div>
                    ) : products.length === 0 ? (
                        <Empty description="Nenhum produto encontrado" style={{padding: '60px 0'}}/>
                    ) : (
                        <>
                            <Row gutter={[24, 24]}>
                                {products.map((product, index) => (
                                    <Col key={product.id} xs={24} sm={12} lg={8}>
                                        <ScrollAnimation delay={index * 0.05}>
                                            <ProductCard
                                                product={product}
                                                onDetail={handleDetail}
                                            />
                                        </ScrollAnimation>
                                    </Col>
                                ))}
                            </Row>

                            {total > PAGE_SIZE && (
                                <PaginationWrapper>
                                    <Pagination
                                        current={page}
                                        pageSize={PAGE_SIZE}
                                        total={total}
                                        onChange={(p) => {
                                            setPage(p);
                                            window.scrollTo({top: 0, behavior: 'smooth'});
                                        }}
                                        showSizeChanger={false}
                                    />
                                </PaginationWrapper>
                            )}
                        </>
                    )}
                </ContentContainer>
            </Section>
        </>
    );
}
